import {
  COMING_SOON_CATEGORY_ID,
  PRODUCTS_INTRO,
  PRODUCT_CATEGORIES,
  productsInCategory,
} from '@/content/products';
import { ActionRow } from '@/components/ActionButton';
import { CategorySidebar } from '@/components/CategorySidebar';
import { ProductCard } from '@/components/ProductCard';
import { Section } from '@/components/Section';
import { Seo } from '@/components/Seo';
import { Reveal } from '@/components/Reveal';
import { Icon } from '@/components/Icon';
import { useUpcomingProducts } from '@/hooks/useUpcomingProducts';

/**
 * 产品目录页。
 *
 * 版式对齐阿里云的产品页：顶部一块居中的标题区，下面是「左侧类目栏 + 右侧卡片墙」。
 * 「精选产品」与其余类目会重复呈现同一批条目，这是刻意的 —— 首次到访的人
 * 通常不知道该往哪一类里找。
 */
export function ProductsPage() {
  const remoteComingSoon = useUpcomingProducts();
  const comingSoon =
    remoteComingSoon && remoteComingSoon.length > 0
      ? remoteComingSoon
      : productsInCategory(COMING_SOON_CATEGORY_ID);

  return (
    <>
      <Seo
        path="/products"
        crumbs={[
          { name: '首页', path: '/' },
          { name: '产品目录', path: '/products' },
        ]}
      />

      <Section tone="hero" spacing="loose" className="border-b border-line">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-5 text-center">
          <span className="label-mono text-brand">{PRODUCTS_INTRO.eyebrow}</span>
          <h1 className="text-[1.875rem] leading-[1.15] font-semibold text-fg md:text-[3.25rem] md:leading-[1.12]">
            {PRODUCTS_INTRO.title}
          </h1>
          <p className="max-w-2xl text-base leading-relaxed text-fg-muted">{PRODUCTS_INTRO.lead}</p>
          <ActionRow actions={PRODUCTS_INTRO.actions} className="justify-center" />
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[14rem_minmax(0,1fr)] lg:gap-12">
          <div className="min-w-0">
            <CategorySidebar />
          </div>

          <div className="flex min-w-0 flex-col gap-16">
            {PRODUCT_CATEGORIES.map((category) => {
              const isComingSoon = category.id === COMING_SOON_CATEGORY_ID;
              const items = isComingSoon ? comingSoon : productsInCategory(category.id);

              return (
                <section
                  key={category.id}
                  id={`cat-${category.id}`}
                  aria-labelledby={`cat-${category.id}-title`}
                >
                  {/* 公告条与页脚指向 #coming-soon，这里补一个同义的锚点。 */}
                  {isComingSoon ? <span id="coming-soon" className="block" aria-hidden="true" /> : null}

                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-grad-tint text-brand">
                      <Icon name={category.icon} size={18} />
                    </span>
                    <div className="min-w-0">
                      <h2
                        id={`cat-${category.id}-title`}
                        className="text-[1.25rem] font-semibold text-fg md:text-[1.5rem]"
                      >
                        {category.name}
                      </h2>
                      <p className="mt-1 text-sm leading-relaxed text-fg-muted">{category.summary}</p>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {items.map((product, index) => (
                      <Reveal key={`${category.id}-${product.id}`} delay={index} as="div">
                        <ProductCard
                          product={product}
                          withAnchor={product.categoryId === category.id}
                          className="h-full"
                        />
                      </Reveal>
                    ))}
                  </div>

                  {isComingSoon ? (
                    <p className="mt-5 flex items-start gap-2.5 rounded-xl border border-line bg-surface-1 bg-grad-soft p-4 text-sm leading-relaxed text-fg-muted">
                      <Icon name="info" size={16} className="mt-0.5 shrink-0 text-brand" />
                      四款产品均在规划阶段，尚未确定发布时间与价格。若您希望它们优先覆盖某个场景，欢迎与我们交流。
                    </p>
                  ) : null}
                </section>
              );
            })}
          </div>
        </div>
      </Section>
    </>
  );
}
